import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, Slider, ToggleButtonGroup, ToggleButton, IconButton, Collapse, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Line, Bar, ComposedChart } from 'recharts';
import { NumericFormat } from 'react-number-format';
import { apiService } from '../../services/apiService';

// 2026-03-21: HC Tax Drag chart — shows Roth vs Taxable distributions with gap = benefit
// Data from backend endpoint, no local computation
const TaxDragHcChart = ({
  convTax, setConvTax,
  returnRate, setReturnRate, dragRate, setDragRate,
  lifeYears, setLifeYears, distMethod, setDistMethod,
  yearsToStart, currentAge
}) => {
  const [chartData, setChartData] = useState(null);
  const [showExplain, setShowExplain] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const data = await apiService.getTaxDragChartData({
        conv_tax: convTax,
        conv_amt: 0,
        return_rate: returnRate / 100,
        drag_rate: dragRate / 100,
        method: distMethod,
        years_to_start: yearsToStart,
        life_years: lifeYears,
        current_age: currentAge,
      });
      // Map to chart format: taxable = tax dist, difference = roth dist - tax dist (for stacked area)
      const baseYear = new Date().getFullYear();
      let cumulRoth = 0, cumulTax = 0;
      const year0 = {
        year: baseYear,
        age: currentAge,
        taxable: 0,
        difference: 0,
        rothBal: convTax,
        taxBal: convTax,
        rothDist: 0,
        taxDist: 0,
        yearlyDiff: 0,
      };
      const mapped = (data.years || []).map(row => {
        cumulRoth += row.hc_roth_dist;
        cumulTax += row.hc_tax_dist;
        return {
          year: row.year,
          age: row.age,
          taxable: Math.round(cumulTax),
          difference: Math.round(cumulRoth - cumulTax),
          rothBal: row.hc_roth_bal,
          taxBal: row.hc_tax_bal,
          rothDist: row.hc_roth_dist,
          taxDist: row.hc_tax_dist,
          yearlyDiff: row.hc_roth_dist - row.hc_tax_dist,
        };
      });
      setChartData({ years: [year0, ...mapped], hc_fv: data.hc_fv, hc_pv: data.hc_pv });
    } catch (err) {
      console.error('HC tax drag chart error:', err);
    }
  }, [convTax, returnRate, dragRate, distMethod, yearsToStart, lifeYears, currentAge]);

  useEffect(() => {
    const timer = setTimeout(fetchData, 300);
    return () => clearTimeout(timer);
  }, [fetchData]);

  if (!chartData || !chartData.years?.length) return null;

  return (
    <Box sx={{ mt: 0, mb: 0, border: '2px solid #b3e5fc', borderRadius: '8px', px: 0, pt: 0, pb: 1 }}>
      <Box sx={{ px: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: -0.5 }}>
        <Typography variant="subtitle1">
          <strong>SRC Alpha (Wrapper Migration)</strong> - Moves Taxable Cash into the Roth
        </Typography>
        <Tooltip title="Click for explanation" arrow>
          <IconButton size="small" onClick={() => setShowExplain(!showExplain)} sx={{ ml: 0.5, color: '#1976d2' }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={showExplain}>
        <Box sx={{ mb: 1, p: 1.5, backgroundColor: '#f0f7ff', borderLeft: '3px solid #1565c0', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.5 }}>
            Paying the ${convTax?.toLocaleString()} conversion tax with cash from your checking or brokerage account effectively moves that money from your taxable account into the tax-free Roth, a lateral wealth move that's economically identical to a Roth contribution. Investment earnings on the ${convTax?.toLocaleString()} will never be taxed, but had you left that money in your brokerage account, its earnings would be taxed every year. The yearly tax on investment earnings this money makes is the 'Tax-Drag' and avoiding it is the reason we invest in IRAs, 401k, etc. The 'Benefit' of these retirement accounts is Tax-Free vs. Tax-Drag growth.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, fontWeight: 'bold', mb: 0.5 }}>
            GRAPH - Quantify the 'Tax-Drag Avoidance Benefit'
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.5 }}>
            First, track how ${convTax?.toLocaleString()} grows tax-free at {returnRate.toFixed(1)}% AND how ${convTax?.toLocaleString()} would-have-grown at the tax-drag rate of {(returnRate * (1 - dragRate / 100)).toFixed(2)}%, red and blue lines. These lines grow and eventually shrink to zero because as the account grows, we and/or our beneficiaries take distributions that deplete the account to zero over time. Second, retirement account distributions increase from the added ${convTax?.toLocaleString()} and it's this increase we're interested in. We compute distribution increases yearly for both the tax-free Roth and the tax-drag balances. For each year, the difference between the tax-free and tax-drag distributions is computed. This benefit is summed each year and the cumulative benefit is represented by the blue bars.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.5 }}>
            If you selected 'RMD Only' (RMD) as your distribution plan, RMDs are taken during your life and upon death remaining balances are distributed to the beneficiary evenly over 10 years. This is good for wealthy folks who don't need extra money during retirement and wish to maximize heir's inheritance. If you selected 'Fixed Annuity' (FA) distributions, an equal amount is distributed each year until death. Using either method, the account depletes to zero, but RMDs grow 10 years longer after death.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.5 }}>
            The last blue bar at the right represents the full nominal dollar benefit of having the ${convTax?.toLocaleString()} in the tax-free Roth vs. leaving it in a taxable account, ${chartData.hc_fv?.toLocaleString()}. Also shown is the present value in today's dollars, ${chartData.hc_pv?.toLocaleString()}.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, fontWeight: 'bold', mb: 0.5 }}>
            OUTSIDE Tax Payment Only
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            The Tax-Drag Avoidance Benefit described here applies only when outside funds are used to pay the conversion tax. Furthermore, this benefit is the 'ONLY' difference between Outside and Inside funded Roth conversions. To reiterate, this difference is a Synthetic or 'Hidden' Roth Contribution.
          </Typography>
        </Box>
      </Collapse>

      {/* Inputs */}
      <Box display="flex" gap={2} flexWrap="nowrap" alignItems="center" sx={{ mb: 0 }}>
        <Box sx={{ minWidth: 145, maxWidth: 145 }}>
          <NumericFormat
            label="Outside Tax Payment"
            value={convTax}
            onValueChange={(v) => setConvTax(v.floatValue || 0)}
            thousandSeparator=","
            prefix="$"
            customInput={TextField}
            size="small"
            variant="outlined"
            fullWidth
            inputProps={{ style: { fontWeight: 'bold' } }}
          />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption">Return: {returnRate.toFixed(1)}%</Typography>
          <Slider
            value={returnRate}
            onChange={(e, v) => setReturnRate(v)}
            min={1} max={15} step={0.5}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
          />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption">Tax Drag: {dragRate.toFixed(1)}%</Typography>
          <Slider
            value={dragRate}
            onChange={(e, v) => setDragRate(v)}
            min={0} max={15} step={0.5}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
          />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption">Life Years: {lifeYears}</Typography>
          <Slider
            value={lifeYears}
            onChange={(e, v) => setLifeYears(v)}
            min={10} max={35} step={1}
            size="small"
            valueLabelDisplay="auto"
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="caption">Dist Method</Typography>
          <ToggleButtonGroup
            value={distMethod}
            exclusive
            onChange={(e, v) => { if (v) setDistMethod(v); }}
            size="small"
          >
            <ToggleButton value="F" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>FA</ToggleButton>
            <ToggleButton value="R" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>RMD</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Terminal values */}
      <Box sx={{ mb: 0.5, textAlign: 'center' }}>
        <Typography variant="body2">
          <strong>Benefit FV:</strong> <span style={{ color: '#2e7d32' }}>${chartData.hc_fv?.toLocaleString()}</span>&nbsp;&nbsp;<strong>PV:</strong> <span style={{ color: '#2e7d32' }}><strong>${chartData.hc_pv?.toLocaleString()}</strong></span>
        </Typography>
      </Box>
      </Box>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={chartData.years} margin={{ top: 5, right: 5, left: 5, bottom: -3 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis yAxisId="left" tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(1) + 'K' : v.toFixed(0)}`} />
          <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(1) + 'K' : v.toFixed(0)}`} label={{ value: 'Cumulative Benefit', angle: 90, position: 'insideRight', offset: 3, style: { fontSize: '0.75rem', fill: '#333', fontWeight: 'bold' } }} />
          <RechartsTooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const row = payload[0]?.payload;
            if (!row) return null;
            return (
              <Box sx={{ backgroundColor: 'white', border: '1px solid #ccc', p: 1, fontSize: '0.875rem' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{label} (age {row.age})</Typography>
                <Box sx={{ color: '#333', display: 'flex', gap: 1 }}><span style={{ minWidth: 45, textAlign: 'right' }}>${(row.taxable + row.difference)?.toLocaleString()}</span> Roth Distributions - Cum</Box>
                <Box sx={{ color: '#d32f2f', display: 'flex', gap: 1 }}><span style={{ minWidth: 45, textAlign: 'right' }}>${row.taxable?.toLocaleString()}</span> Tax-Drag Distributions - Cum</Box>
                <Box sx={{ color: '#2e7d32', fontWeight: 'bold', display: 'flex', gap: 1 }}><span style={{ minWidth: 45, textAlign: 'right' }}>${row.difference?.toLocaleString()}</span> Tax-Free Growth Benefit - Cum</Box>
                <Box sx={{ color: '#888', mt: 0.5 }}>Distribution: Roth ${Math.round(row.rothDist)?.toLocaleString()} | Tax-Drag ${Math.round(row.taxDist)?.toLocaleString()} | Diff ${Math.round(row.yearlyDiff)?.toLocaleString()}</Box>
                <Box sx={{ color: '#1565c0', mt: 0.5 }}>Balance: Roth ${row.rothBal?.toLocaleString()} | Tax-Drag ${row.taxBal?.toLocaleString()}</Box>
              </Box>
            );
          }} />
          <Legend height={25} />
          <Bar yAxisId="right" dataKey="difference" name="Tax-Drag Benefit - Cum" fill="#90caf9" stroke="#1565c0" />
          <Line yAxisId="left" type="monotone" dataKey="rothBal" name="Roth Balance" stroke="#1565c0" strokeWidth={1.5} dot={false} />
          <Line yAxisId="left" type="monotone" dataKey="taxBal" name="Tax-Drag Balance" stroke="#d32f2f" strokeWidth={1.5} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TaxDragHcChart;
